import { useState } from 'react';
import { useClientPhoto } from '@/hooks/useClientPhoto';
import { ClientPhotoLightbox } from '@/components/ClientPhotoLightbox';
import { cn } from '@/lib/utils';

interface ClientAvatarProps {
  name: string;
  photoPath?: string | null;
  className?: string;
  textClassName?: string;
  zoomable?: boolean;
}

/**
 * Shows the client profile photo (via signed URL) when available,
 * otherwise falls back to initials. When zoomable, clicking the photo
 * opens an enlarged view.
 */
export function ClientAvatar({
  name,
  photoPath,
  className,
  textClassName,
  zoomable = true,
}: ClientAvatarProps) {
  const { data: url } = useClientPhoto(photoPath);
  const [open, setOpen] = useState(false);

  const initials = name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  if (url) {
    const image = (
      <img
        src={url}
        alt={name}
        className={cn('rounded-full object-cover shrink-0', className)}
      />
    );

    if (!zoomable) {
      return image;
    }

    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`Ampliar foto de ${name}`}
          className="rounded-full shrink-0 cursor-zoom-in transition-shadow hover:ring-2 hover:ring-primary/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {image}
        </button>
        <ClientPhotoLightbox open={open} onOpenChange={setOpen} src={url} name={name} />
      </>
    );
  }

  return (
    <div
      className={cn(
        'rounded-full gradient-primary flex items-center justify-center shrink-0',
        className
      )}
    >
      <span className={cn('font-semibold text-primary-foreground', textClassName)}>{initials}</span>
    </div>
  );
}
